import { type HttpClient, segment } from '../http';

import { type Friendship } from './friendships.types';

/**
 * Who the reader knows, and who is asking.
 *
 * Warning: a friendship is addressed by **the other person's user id**, never
 * by an id of its own. The pair is the key in the database, and the routes
 * follow it.
 *
 * @param http Transport supplied by the application.
 * @returns The `friendships` domain of the API client.
 */
export function createFriendshipsApi(http: HttpClient) {
  return {
    /**
     * Everybody the reader is friends with.
     *
     * @returns The accepted friendships.
     */
    list: () => http.get<readonly Friendship[]>('/friendships'),

    /**
     * The requests waiting for an answer.
     *
     * @returns The pending friendships somebody else started.
     */
    received: () =>
      http.get<readonly Friendship[]>('/friendships/requests/received'),

    /**
     * The requests the reader started.
     *
     * @returns The pending friendships they are waiting on.
     */
    sent: () => http.get<readonly Friendship[]>('/friendships/requests/sent'),

    /**
     * Asks somebody to be friends.
     *
     * @param userId The other person.
     * @returns The friendship as it now stands.
     */
    request: (userId: string) =>
      http.post<Friendship>(`/friendships/${segment(userId)}`, {}),

    /**
     * Says yes.
     *
     * @param userId The person who asked.
     * @returns The friendship as it now stands.
     */
    accept: (userId: string) =>
      http.post<Friendship>(`/friendships/${segment(userId)}/accept`, {}),

    /**
     * Says no, or stops being friends.
     *
     * Warning: one route for both. A declined request is not stored as
     * declined, it is removed — so the same call ends a friendship and turns
     * down a request.
     *
     * @param userId The other person.
     * @returns Nothing.
     */
    remove: (userId: string) =>
      http.delete<void>(`/friendships/${segment(userId)}`),

    /**
     * Blocks somebody.
     *
     * Warning: the API has carried this since the friendships module was
     * written, and **this package never exposed it** — which is why nothing in
     * the application could reach it, and why blocking was unavailable to the
     * people who most needed it.
     *
     * ⚠️ Blocking is symmetric in effect: neither can open a conversation with
     * the other afterwards, and neither can write into the direct one they
     * already had. It does not silence either of them in a household or a
     * group both belong to — leaving is the answer there.
     *
     * @param userId The person being blocked.
     * @returns Nothing.
     */
    block: (userId: string) =>
      http.post<void>(`/friendships/${segment(userId)}/block`, {}),

    /** Everybody the reader has blocked. */
    blocked: () => http.get<readonly Friendship[]>('/friendships/blocked'),
  };
}

export type FriendshipsApi = ReturnType<typeof createFriendshipsApi>;
